/**
 * WordPress-style hooks system for Pixel Manager
 *
 * Provides a filter API that allows developers to modify event data
 * at various points in the event processing pipeline.
 *
 * Available Filters:
 *
 * 1. pmw_event_payload_pre (payload, eventName)
 *    - Runs before pixel adaptation
 *    - Use case: Add global tracking params, modify core data
 *
 * 2. pmw_pixel_data_{pixelName} (pixelData, eventName, eventData, context)
 *    - Runs after each pixel's adapter
 *    - Use case: Modify pixel-specific data (e.g., pmw_pixel_data_facebook)
 *
 * 3. pmw_event_payload_{eventName} (payload, eventData)
 *    - Runs for specific event types
 *    - Use case: Modify all pixels for a specific event (e.g., pmw_event_payload_add_to_cart)
 *
 * 4. pmw_event_payload_post (payload, eventName)
 *    - Runs after all processing, before dispatch
 *    - Use case: Final validation, logging, debugging
 *
 * 5. pmw_pixel_firing_decision (decision, pixelName, eventName, mode)
 *    - Runs when determining whether to fire browser/server
 *    - decision: { fire_browser: boolean, fire_server: boolean }
 *    - Use case: Force server-only for specific pixels, disable certain pixels per-event
 *
 * @since 1.51.0
 */

(function (pmw, undefined) {

	// Initialize pmw namespace if it doesn't exist
	window.pmw = window.pmw || {};

	/**
	 * Hook system for filtering event data
	 */
	pmw.hooks = {
		filters: {},

		/**
		 * Add a filter callback
		 *
		 * @param {string} hookName - The filter hook name (e.g., 'pmw_event_payload')
		 * @param {string} namespace - Unique identifier for your filter (e.g., 'my-plugin')
		 * @param {function} callback - The filter function that modifies the value
		 * @param {number} priority - Execution order, lower numbers run first (default: 10)
		 *
		 * @example
		 * // Modify Facebook pixel data
		 * pmw.hooks.addFilter('pmw_pixel_data_facebook', 'my-plugin', (data) => {
		 *   data.custom_param = 'my_value';
		 *   return data;
		 * }, 10);
		 *
		 * @example
		 * // Force server-only firing for Facebook
		 * pmw.hooks.addFilter('pmw_pixel_firing_decision', 'my-plugin', (decision, pixelName) => {
		 *   if (pixelName === 'facebook') {
		 *     return { fire_browser: false, fire_server: true };
		 *   }
		 *   return decision;
		 * });
		 */
		addFilter: function (hookName, namespace, callback, priority = 10) {
			if (!hookName || typeof hookName !== "string") {
				pmw.console.error("Invalid hook name provided to addFilter");
				return;
			}

			if (!namespace || typeof namespace !== "string") {
				pmw.console.error("Invalid namespace provided to addFilter");
				return;
			}

			if (typeof callback !== "function") {
				pmw.console.error("Invalid callback provided to addFilter");
				return;
			}

			if (!this.filters[hookName]) {
				this.filters[hookName] = [];
			}

			this.filters[hookName].push({
				namespace: namespace,
				callback : callback,
				priority : priority,
			});

			// Sort by priority (ascending order)
			this.filters[hookName].sort((a, b) => a.priority - b.priority);

			pmw.console.log(`Filter added: ${hookName} (${namespace}) with priority ${priority}`);
		},

		/**
		 * Apply all registered filters to a value
		 *
		 * @param {string} hookName - The filter hook name
		 * @param {*} value - The value to filter
		 * @param {...*} args - Additional arguments passed to filter callbacks
		 * @returns {*} The filtered value
		 *
		 * @example
		 * let payload = pmw.hooks.applyFilters('pmw_event_payload_pre', payload, eventName);
		 */
		applyFilters: function (hookName, value, ...args) {
			if (!this.filters[hookName] || this.filters[hookName].length === 0) {
				return value;
			}

			let filteredValue = value;

			for (const filter of this.filters[hookName]) {
				try {
					const result = filter.callback(filteredValue, ...args);

					// Ensure callback returns a value
					if (result !== undefined) {
						filteredValue = result;
					} else {
						pmw.console.warn(`Filter ${filter.namespace} on ${hookName} returned undefined, keeping previous value`);
					}
				} catch (e) {
					pmw.console.error(`Filter error in ${filter.namespace} on ${hookName}:`, e);
					// Continue with the previous value if this filter fails
				}
			}

			return filteredValue;
		},

		/**
		 * Remove a specific filter
		 *
		 * @param {string} hookName - The filter hook name
		 * @param {string} namespace - The namespace of the filter to remove
		 *
		 * @example
		 * pmw.hooks.removeFilter('pmw_pixel_data_facebook', 'my-plugin');
		 */
		removeFilter: function (hookName, namespace) {
			if (!this.filters[hookName]) {
				return;
			}

			const initialLength    = this.filters[hookName].length;
			this.filters[hookName] = this.filters[hookName].filter(f => f.namespace !== namespace);

			if (this.filters[hookName].length < initialLength) {
				pmw.console.log(`Filter removed: ${hookName} (${namespace})`);
			}
		},

		/**
		 * Remove all filters for a specific hook
		 *
		 * @param {string} hookName - The filter hook name
		 */
		removeAllFilters: function (hookName) {
			if (this.filters[hookName]) {
				delete this.filters[hookName];
				pmw.console.log(`All filters removed for: ${hookName}`);
			}
		},

		/**
		 * Check if a specific hook has any filters
		 *
		 * @param {string} hookName - The filter hook name
		 * @param {string} namespace - Optional: check for a specific namespace
		 * @returns {boolean}
		 */
		hasFilter: function (hookName, namespace = null) {
			if (!this.filters[hookName] || this.filters[hookName].length === 0) {
				return false;
			}

			if (namespace) {
				return this.filters[hookName].some(f => f.namespace === namespace);
			}

			return true;
		},

		/**
		 * Get all registered filters for a hook
		 *
		 * @param {string} hookName - The filter hook name
		 * @returns {Array} Array of filter objects
		 */
		getFilters: function (hookName) {
			return this.filters[hookName] || [];
		},
	};

	// Backward compatibility: create pmw.hooks as an alias to pmw.hooks
	pmw.hooks = pmw.hooks;

}(window.pmw = window.pmw || {}));
